import { SCHEMA } from '@vedalib/editor/lib/brand'
import { FileUsageTypeEnum } from '@vedalib/editor/lib/files'
import React, { useCallback, useMemo } from 'react'

import Padding from 'components/Editor/Toolbar/Padding'
import { LayoutScroll } from 'components/LayoutPage'
import {
  FieldColorPicker,
  FieldFileId,
  FieldSegmented,
  FieldSlider,
  FormField,
} from 'components/formKit'
import { TileButton } from 'components/uiKit/Button'
import { Typography } from 'components/uiKit/Typography'
import { BrandFieldShadow, BrandTagEdited } from 'services/Branding/components'
import { BORDER_OPTIONS } from 'services/Branding/constants'
import {
  useBrandColorOptions,
  useShowBorder,
  useThemeValueGetter,
} from 'services/Branding/fieldConfigs/useFieldConfigs'
import { isAnyEdited } from 'services/Branding/fieldConfigs/utils'
import { genSchema, genBrandField } from 'services/Branding/genSchema'
import { useUpdateSchema } from 'services/Branding/hooks'
import { BrandFormEnum } from 'services/Branding/types'
import { t } from 'services/Translation'

import type { TestFeedbackFormBrandType } from './testFeedbackTypes'

export const STYLE_FORM_SCHEMA = genSchema(SCHEMA.testFeedback.style, {
  common: {
    borderRadius: genBrandField({
      as: FieldSlider,
      label: t('input.label.borderRadius'),
      props: { min: 0, max: 50, showInput: true },
      useConfigs: [useThemeValueGetter],
    }),
  },
  infoIcon: {
    backgroundImage: genBrandField({
      as: FieldFileId,
      label: t('elements.testFeedback.schema.infoIcon'),
      layout: 'vertical',
      props: { fileType: FileUsageTypeEnum.image, preview: true },
    }),
  },
  infoPanel: {
    backgroundColor: genBrandField({
      as: FieldColorPicker<string | null>,
      label: t('input.label.backgroundColor'),
      props: { transparent: true, labeled: true },
      useConfigs: [useBrandColorOptions],
    }),
    borderStyle: genBrandField({
      as: FieldSegmented<'none' | 'solid'>,
      label: t('input.label.border'),
      props: { options: BORDER_OPTIONS },
      useConfigs: [useThemeValueGetter],
    }),
    borderColor: genBrandField({
      as: FieldColorPicker<string | null>,
      label: t('input.label.borderColor'),
      useConfigs: [useBrandColorOptions, useShowBorder],
    }),
    borderWidth: genBrandField({
      as: FieldSlider,
      label: t('input.label.borderWidth'),
      props: { min: 1, max: 8, showInput: true },
      useConfigs: [useShowBorder],
    }),
  },
  successIcon: {
    backgroundImage: genBrandField({
      as: FieldFileId,
      label: t('elements.testFeedback.schema.successIcon'),
      layout: 'vertical',
      props: { fileType: FileUsageTypeEnum.image, preview: true },
    }),
  },
  successPanel: {
    backgroundColor: genBrandField({
      as: FieldColorPicker<string | null>,
      label: t('input.label.backgroundColor'),
      props: { transparent: true, labeled: true },
      useConfigs: [useBrandColorOptions],
    }),
    borderStyle: genBrandField({
      as: FieldSegmented<'none' | 'solid'>,
      label: t('input.label.border'),
      props: { options: BORDER_OPTIONS },
      useConfigs: [useThemeValueGetter],
    }),
    borderColor: genBrandField({
      as: FieldColorPicker<string | null>,
      label: t('input.label.borderColor'),
      useConfigs: [useBrandColorOptions, useShowBorder],
    }),
    borderWidth: genBrandField({
      as: FieldSlider,
      label: t('input.label.borderWidth'),
      props: { min: 1, max: 8, showInput: true },
      useConfigs: [useShowBorder],
    }),
  },
  failIcon: {
    backgroundImage: genBrandField({
      as: FieldFileId,
      label: t('elements.testFeedback.schema.failIcon'),
      layout: 'vertical',
      props: { fileType: FileUsageTypeEnum.image, preview: true },
    }),
  },
  failPanel: {
    backgroundColor: genBrandField({
      as: FieldColorPicker<string | null>,
      label: t('input.label.backgroundColor'),
      props: { transparent: true, labeled: true },
      useConfigs: [useBrandColorOptions],
    }),
    borderStyle: genBrandField({
      as: FieldSegmented<'none' | 'solid'>,
      label: t('input.label.border'),
      props: { options: BORDER_OPTIONS },
      useConfigs: [useThemeValueGetter],
    }),
    borderColor: genBrandField({
      as: FieldColorPicker<string | null>,
      label: t('input.label.borderColor'),
      useConfigs: [useBrandColorOptions, useShowBorder],
    }),
    borderWidth: genBrandField({
      as: FieldSlider,
      label: t('input.label.borderWidth'),
      props: { min: 1, max: 8, showInput: true },
      useConfigs: [useShowBorder],
    }),
  },
  effects: {
    boxShadow: genBrandField({
      as: BrandFieldShadow,
      label: t('elements.testFeedback.schema.boxShadow'),
      useConfigs: [useThemeValueGetter],
    }),
  },
})

const TestFeedbackFormStyle: TestFeedbackFormBrandType = ({
  device,
  subConfigs,
  edited,
  setForm,
}) => {
  const path = useMemo(() => ['style', device], [device])
  const schema = useUpdateSchema(path, subConfigs, STYLE_FORM_SCHEMA)

  const handleSetTypography = useCallback(() => setForm?.(BrandFormEnum.TYPOGRAPHY), [setForm])

  return (
    <LayoutScroll sizeAutoCapable>
      <Padding bottomSpace gap sides>
        <FormField config={schema.common.borderRadius} />
        <Typography weight='medium'>{t('elements.testFeedback.tags.infoPanel')}</Typography>
        <FormField config={schema.infoIcon.backgroundImage} />
        <FormField config={schema.infoPanel.backgroundColor} />
        <FormField config={schema.infoPanel.borderStyle} />
        <FormField config={schema.infoPanel.borderColor} />
        <FormField config={schema.infoPanel.borderWidth} />

        <Typography weight='medium'>{t('elements.testFeedback.tags.successPanel')}</Typography>
        <FormField config={schema.successIcon.backgroundImage} />
        <FormField config={schema.successPanel.backgroundColor} />
        <FormField config={schema.successPanel.borderStyle} />
        <FormField config={schema.successPanel.borderColor} />
        <FormField config={schema.successPanel.borderWidth} />

        <Typography weight='medium'>{t('elements.testFeedback.tags.failPanel')}</Typography>
        <FormField config={schema.failIcon.backgroundImage} />
        <FormField config={schema.failPanel.backgroundColor} />
        <FormField config={schema.failPanel.borderStyle} />
        <FormField config={schema.failPanel.borderColor} />
        <FormField config={schema.failPanel.borderWidth} />

        <Typography weight='medium'>{t('style.effects')}</Typography>
        <FormField config={schema.effects.boxShadow} />

        <TileButton
          description={t('uiKit.button.richTextDescription')}
          icon='otherRight'
          name='showFontForm'
          onClick={handleSetTypography}
        >
          <Typography weight='medium'>{t('page.brand.tab.typography')}</Typography>
          {isAnyEdited(edited.font) && <BrandTagEdited />}
        </TileButton>
      </Padding>
    </LayoutScroll>
  )
}

export default React.memo(TestFeedbackFormStyle) as typeof TestFeedbackFormStyle
